import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Alert,
  Platform,
  TextInput as RNTextInput,
  Image,
  ImageStyle,
  Animated,
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useAuth, authKeys } from '../../hooks/useAuth';
import { RootStackParamList } from '../../types/navigation';
import useColourStore from '../../store/useColourStore';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { useFeatureFlag, isFeatureEnabled } from '../../hooks/useFeatureFlags';
import UserAvatar from '../../components/shared/UserAvatar';
import ResponsiveContainer from '../../components/shared/ResponsiveContainer';
import { Button } from '../../components/common';
import TextField from '../../components/common/TextField';
import {
  getRecentAnalysis,
  getRecentAnalysisFromCacheSync,
  type RecentAnalysisResult,
  type SavedPaletteExtras,
} from '../../api/colours';
import JewelryMetalSwatch from '../../components/shared/JewelryMetalSwatch';
import * as ImagePicker from 'expo-image-picker';
import { usersAPI } from '../../api/users';
import { moodBoardAPI } from '../../api/mood_boards';
import { getWardrobeItems } from '../../api/wardrobe';
import { useQueryClient } from '@tanstack/react-query';
import { palette } from '../../utils/palette';
import PageHero from '../../components/shared/PageHero';
import InviteModal from '../../components/shared/InviteModal';
import { invitesAPI } from '../../api/invites';

export interface ProfileScreenProps {
  onTabPress?: (tab: string) => void;
}

const ProfileScreen = ({ onTabPress }: ProfileScreenProps) => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { user, isAuthenticated, refreshUser, signOut } = useAuth();
  const { data: pricingFlag } = useFeatureFlag('pricing_subscription');
  const paymentsKillSwitchOn = isFeatureEnabled(
    pricingFlag,
    'pricing_subscription'
  );
  const queryClient = useQueryClient();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const { isMobile } = useResponsiveLayout();
  const scrollTopPadding = isMobile ? insets.top + 12 : 16;
  const { profiles, error, fetchProfiles, clearError } = useColourStore();

  const [recentAnalysis, setRecentAnalysis] =
    useState<RecentAnalysisResult | null>(null);
  const [isLoadingRecent, setIsLoadingRecent] = useState(false);
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [profileImageUri, setProfileImageUri] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [savingBio, setSavingBio] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const toastAnim = useState(new Animated.Value(100))[0];
  // Badges + moodboard hidden for now
  // const [userMoodboards, setUserMoodboards] = useState<any[]>([]);
  // const [isLoadingMoodboards, setIsLoadingMoodboards] = useState(false);
  const [editedBio, setEditedBio] = useState('');
  const [editedName, setEditedName] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const nameInputRef = useRef<RNTextInput>(null);
  const bioInputRef = useRef<RNTextInput>(null);

  const [toolkitStats, setToolkitStats] = useState<{
    analyses: number;
    moodboards: number;
    wardrobe: number;
  }>({ analyses: 0, moodboards: 0, wardrobe: 0 });
  const [toolkitLoading, setToolkitLoading] = useState(false);

  const inviteCode = useMemo(() => {
    if (!user?.id) return '';
    const generateCode = (input: string): string => {
      let hash = 0;
      for (let i = 0; i < input.length; i++) {
        const char = input.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash;
      }
      const hashStr = Math.abs(hash).toString(36).toUpperCase();
      const userIdStr = user.id.toString().padStart(4, '0');
      return (hashStr + userIdStr).substring(0, 12);
    };
    return generateCode(`invite_${user.id}_${user.email || 'user'}`);
  }, [user?.id, user?.email]);

  const inviteLink = useMemo(() => {
    if (!inviteCode) return '';
    const baseUrl =
      Platform.OS === 'web' && typeof window !== 'undefined'
        ? window.location.origin
        : 'https://mycoloursense.com';
    return `${baseUrl}/invite/${inviteCode}`;
  }, [inviteCode]);

  const handleInvitePress = async () => {
    if (inviteCode) {
      try {
        await invitesAPI.create(inviteCode);
      } catch {
        // already exists or failed – still show modal
      }
    }
    setShowInviteModal(true);
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800)
      return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return `${Math.floor(diffInSeconds / 604800)}w ago`;
  };

  const fetchRecentAnalysis = async (showSkeleton = true) => {
    try {
      if (showSkeleton) setIsLoadingRecent(true);
      const analysis = await getRecentAnalysis();
      setRecentAnalysis(analysis);
    } catch {
      // Fetch failed
    } finally {
      if (showSkeleton) setIsLoadingRecent(false);
    }
  };

  // const fetchUserMoodboards = async () => {
  //   if (!user?.id) return;
  //   try {
  //     setIsLoadingMoodboards(true);
  //     const boards = await moodBoardAPI.getUserMoodBoards(Number(user.id), { limit: 100 });
  //     setUserMoodboards(boards.items);
  //   } catch (error) {
  //     console.error('Error fetching user moodboards:', error);
  //   } finally {
  //     setIsLoadingMoodboards(false);
  //   }
  // };

  useEffect(() => {
    if (!isAuthenticated) {
      navigation.replace('Login');
      return;
    }

    fetchProfiles();
  }, [isAuthenticated, fetchProfiles, navigation]);

  useEffect(() => {
    if (!user?.id) return;
    void (async () => {
      const cached = getRecentAnalysisFromCacheSync(user.id);
      if (cached) setRecentAnalysis(cached);
      await fetchRecentAnalysis(!cached);
    })();
  }, [user?.id]);

  useEffect(() => {
    if (user) {
      setProfileImageUri(user.profileImageUrl || null);
      setEditedBio(user.bio || '');
      setEditedName(user.name || '');
    }
  }, [user]);

  useEffect(() => {
    if (isEditingName) {
      const timer = setTimeout(() => nameInputRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [isEditingName]);

  useEffect(() => {
    if (isEditingBio) {
      const timer = setTimeout(() => bioInputRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [isEditingBio]);

  useEffect(() => {
    toastAnim.setValue(100);
  }, [toastAnim]);

  const fetchToolkitStats = React.useCallback(async () => {
    if (!user?.id) return;
    setToolkitLoading(true);
    try {
      const userId =
        typeof user.id === 'number' ? user.id : parseInt(String(user.id), 10);
      const [moodboardsRes, wardrobeItems] = await Promise.all([
        moodBoardAPI.getMoodBoards({ user_id: userId, limit: 1, offset: 0 }),
        getWardrobeItems().catch(() => []),
      ]);
      setToolkitStats((prev) => ({
        ...prev,
        moodboards: moodboardsRes?.total ?? 0,
        wardrobe: Array.isArray(wardrobeItems) ? wardrobeItems.length : 0,
      }));
    } catch {
      // Keep previous counts
    } finally {
      setToolkitLoading(false);
    }
  }, [user?.id]);

  const toolkitStatsWithAnalyses = useMemo(
    () => ({
      ...toolkitStats,
      analyses: profiles?.length ?? 0,
    }),
    [toolkitStats, profiles?.length]
  );

  useFocusEffect(
    React.useCallback(() => {
      if (!isAuthenticated) return;

      const refreshData = async () => {
        queryClient.invalidateQueries({ queryKey: authKeys.user() });
        try {
          await refreshUser();
        } catch {
          // Refresh failed
        }
        if (user?.id) {
          const cached = getRecentAnalysisFromCacheSync(user.id);
          if (cached) setRecentAnalysis(cached);
          fetchRecentAnalysis(!cached);
        }
        fetchProfiles();
      };

      refreshData();
    }, [isAuthenticated, user, refreshUser, queryClient, fetchProfiles])
  );

  useEffect(() => {
    if (!user?.id) return;
    const timer = setTimeout(() => {
      void fetchToolkitStats();
    }, 0);
    return () => clearTimeout(timer);
  }, [user?.id, fetchProfiles, fetchToolkitStats]);

  const showToastMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    Animated.timing(toastAnim, {
      toValue: 0,
      duration: 300,
      useNativeDriver: false,
    }).start(() => {
      setTimeout(() => {
        Animated.timing(toastAnim, {
          toValue: 100,
          duration: 300,
          useNativeDriver: false,
        }).start(() => {
          setShowToast(false);
        });
      }, 2000);
    });
  };

  const pickImage = async () => {
    try {
      const { status } =
        await ImagePicker.requestMediaLibraryPermissionsAsync();

      if (status !== 'granted') {
        Alert.alert(
          'Permission Required',
          'Please grant permission to access your photo library.'
        );
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (
        result &&
        !result.canceled &&
        result.assets &&
        result.assets.length > 0
      ) {
        const newImageUri = result.assets[0].uri;
        setProfileImageUri(newImageUri);
        // Auto-upload image
        await uploadProfileImage(newImageUri);
      }
    } catch {
      Alert.alert('Error', 'Failed to select image. Please try again.');
    }
  };

  const takePhoto = async () => {
    try {
      const { status } = await ImagePicker.requestCameraPermissionsAsync();

      if (status !== 'granted') {
        Alert.alert(
          'Permission Required',
          'Please grant permission to access your camera.'
        );
        return;
      }

      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (
        result &&
        !result.canceled &&
        result.assets &&
        result.assets.length > 0
      ) {
        const newImageUri = result.assets[0].uri;
        setProfileImageUri(newImageUri);
        // Auto-upload image
        await uploadProfileImage(newImageUri);
      }
    } catch {
      Alert.alert('Error', 'Failed to take photo. Please try again.');
    }
  };

  const showImagePickerOptions = () => {
    if (Platform.OS === 'web') {
      pickImage();
      return;
    }

    Alert.alert(
      'Change Profile Photo',
      'Choose an option',
      [
        { text: 'Camera', onPress: takePhoto },
        { text: 'Photo Library', onPress: pickImage },
        { text: 'Cancel', style: 'cancel' },
      ],
      { cancelable: true }
    );
  };

  const uploadProfileImage = async (imageUri: string) => {
    try {
      setUploading(true);
      const profileImageUrl = await usersAPI.uploadProfileImage(imageUri);

      // Update profile with new image
      await usersAPI.updateProfile({ profile_image_url: profileImageUrl });
      await refreshUser();
      showToastMessage('Profile picture updated!');
    } catch {
      Alert.alert('Error', 'Failed to upload image. Please try again.');
      // Revert to original image
      setProfileImageUri(user?.profileImageUrl || null);
    } finally {
      setUploading(false);
    }
  };

  const handleSaveBio = async () => {
    try {
      setSavingBio(true);

      if (editedBio !== (user?.bio || '')) {
        await usersAPI.updateProfile({ bio: editedBio });
        await refreshUser();
        showToastMessage('Bio updated successfully!');
      }
      setIsEditingBio(false);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ||
        (err as { message?: string })?.message ||
        'Failed to update bio. Please try again.';
      Alert.alert('Error', message);
    } finally {
      setSavingBio(false);
    }
  };

  const handleCancelBio = () => {
    setEditedBio(user?.bio || '');
    setIsEditingBio(false);
  };

  const handleSaveName = async () => {
    try {
      setSavingName(true);

      const trimmedName = editedName.trim();
      if (trimmedName !== (user?.name || '')) {
        if (!trimmedName) {
          Alert.alert('Error', 'Name cannot be empty.');
          return;
        }
        await usersAPI.updateProfile({ name: trimmedName });
        await refreshUser();
        showToastMessage('Name updated successfully!');
      }
      setIsEditingName(false);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ||
        (err as { message?: string })?.message ||
        'Failed to update name. Please try again.';
      Alert.alert('Error', message);
    } finally {
      setSavingName(false);
    }
  };

  const handleCancelName = () => {
    setEditedName(user?.name || '');
    setIsEditingName(false);
  };

  useEffect(() => {
    if (error) {
      Alert.alert('Error', error, [{ text: 'OK', onPress: clearError }]);
    }
  }, [error, clearError]);

  const navigateToColorAnalysis = () => {
    // For web layout, use onTabPress and also navigate to ensure URL updates
    if (onTabPress && Platform.OS === 'web') {
      onTabPress('ColourAnalysis');
      // Also navigate to ensure the route is properly set
      navigation.navigate('MainTabs', {
        initialTab: 'ColourAnalysis',
        section: 'color-analysis',
      });
      return;
    }

    // For mobile, navigate to MainTabs with initialTab
    // The BottomTabNavigator will handle mapping to ColourAnalysisMobile
    navigation.navigate('MainTabs', {
      initialTab: 'ColourAnalysis',
    });
  };

  // Subscription label: show Plus/Pro only; never show "Trial" in Profile (trial is for Plus/Pro only)
  const planLabel =
    user?.subscription_plan === 'pro'
      ? 'Pro'
      : user?.subscription_plan === 'plus'
        ? 'Plus'
        : user?.subscription_plan === 'freemium_trial'
          ? 'Plus' // Trial of Plus – do not show "Trial" in Profile
          : user?.subscription_plan === 'pay_per_scan'
            ? 'Pay-per-scan'
            : 'Free';

  const lastAnalysisAt =
    (recentAnalysis as { created_at?: string } | null)?.created_at ||
    (profiles?.[0] as { created_at?: string } | undefined)?.created_at;

  const activityDiscoverItems = [
    {
      key: 'analysis',
      icon: 'color-palette-outline' as const,
      label: 'Colour analysis',
      value: toolkitStatsWithAnalyses.analyses,
      valueLabel:
        toolkitStatsWithAnalyses.analyses === 1
          ? '1 analysis'
          : `${toolkitStatsWithAnalyses.analyses} analyses`,
      lastUsed: lastAnalysisAt ? formatTimeAgo(lastAnalysisAt) : null,
      color: palette.softViolet,
      navigate: () =>
        navigation.navigate('MainTabs', { initialTab: 'ColourAnalysis' }),
    },
    {
      key: 'drape',
      icon: 'shirt-outline' as const,
      label: 'Virtual drape test',
      value: user?.virtual_drape_used_at ? 'used' : null,
      valueLabel: user?.virtual_drape_used_at
        ? formatTimeAgo(user.virtual_drape_used_at)
        : 'Not used yet',
      lastUsed: user?.virtual_drape_used_at
        ? formatTimeAgo(user.virtual_drape_used_at)
        : null,
      color: palette.cyanLimeGreen,
      navigate: () =>
        navigation.navigate('MainTabs', { initialTab: 'VirtualDrapeTest' }),
    },
    {
      key: 'palette',
      icon: 'albums-outline' as const,
      label: 'Palette generator',
      value: user?.color_generator_used_at ? 'used' : null,
      valueLabel: user?.color_generator_used_at
        ? formatTimeAgo(user.color_generator_used_at)
        : 'Not used yet',
      lastUsed: user?.color_generator_used_at
        ? formatTimeAgo(user.color_generator_used_at)
        : null,
      color: palette.brightMatteRed,
      navigate: () =>
        navigation.navigate('MainTabs', { initialTab: 'ColorGenerator' }),
    },
    {
      key: 'colorLibrary',
      icon: 'library-outline' as const,
      label: 'Colour library',
      value: null,
      valueLabel: 'Explore colours',
      lastUsed: null,
      color: palette.strongCyan,
      navigate: () =>
        navigation.navigate('MainTabs', {
          initialTab: 'ColorLibrary',
          ...(Platform.OS === 'web' && { section: 'color-library' }),
        }),
    },
  ];

  const trackableToolsCount = 3; // analyses, drape, palette (colour library not tracked)
  const toolsUsedCount = [
    toolkitStatsWithAnalyses.analyses > 0,
    !!user?.virtual_drape_used_at,
    !!user?.color_generator_used_at,
  ].filter(Boolean).length;
  const totalTools = 4; // discover section items
  const activityProgressLabel =
    toolsUsedCount === trackableToolsCount
      ? 'All tools used'
      : `${toolsUsedCount} of ${totalTools} tools used`;

  const renderIdentityBlock = () => (
    <View
      style={[
        styles.identityCard,
        isMobile && styles.identityCardMobile,
        { backgroundColor: colors.card, borderColor: colors.border },
      ]}
    >
      <View style={styles.identityRow}>
        <TouchableOpacity
          onPress={showImagePickerOptions}
          disabled={uploading}
          style={styles.avatarEditWrapper}
        >
          {profileImageUri ? (
            <Image
              source={{ uri: profileImageUri }}
              style={
                [
                  styles.editableAvatar,
                  { borderColor: colors.border },
                ] as ImageStyle[]
              }
            />
          ) : (
            <UserAvatar
              size={72}
              imageUrl={user?.profileImageUrl}
              name={user?.name}
              email={user?.email}
              showActiveStatus={true}
              isActive={true}
            />
          )}
          {uploading && (
            <View style={styles.uploadingOverlay}>
              <ActivityIndicator size="small" color={colors.primary} />
            </View>
          )}
          <View
            style={[styles.editIconBadge, { backgroundColor: colors.primary }]}
          >
            <Ionicons name="camera" size={14} color="white" />
          </View>
        </TouchableOpacity>
        <View style={styles.identityInfo}>
          {isEditingName ? (
            <View style={styles.nameEditContainer}>
              <View style={styles.nameEditRow}>
                <TextField
                  ref={nameInputRef}
                  value={editedName}
                  onChangeText={setEditedName}
                  placeholder="Your name"
                  maxLength={50}
                  containerStyle={styles.nameInput as Record<string, unknown>}
                  style={styles.nameInputField}
                />
                <View style={styles.nameEditActions}>
                  <TouchableOpacity
                    onPress={handleCancelName}
                    style={[
                      styles.nameActionButton,
                      { borderColor: colors.border },
                    ]}
                    disabled={savingName}
                  >
                    <Ionicons name="close" size={16} color={colors.secondary} />
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={handleSaveName}
                    style={[
                      styles.nameActionButton,
                      styles.saveNameButton,
                      { backgroundColor: colors.primary },
                    ]}
                    disabled={savingName}
                  >
                    {savingName ? (
                      <ActivityIndicator size="small" color="white" />
                    ) : (
                      <Ionicons name="checkmark" size={16} color="white" />
                    )}
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          ) : (
            <TouchableOpacity
              onPress={() => setIsEditingName(true)}
              style={styles.nameRow}
              activeOpacity={0.7}
            >
              <Text style={[styles.userName, { color: colors.text }]}>
                {user?.name || user?.email?.split('@')[0] || 'User'}
              </Text>
              <Ionicons
                name="create-outline"
                size={16}
                color={colors.secondary}
              />
            </TouchableOpacity>
          )}
          {user?.email && (
            <Text
              style={[styles.userEmail, { color: colors.secondary }]}
              numberOfLines={1}
            >
              {user.email}
            </Text>
          )}
          {hasAnalysisData && (seasonLabel || undertoneLabel) ? (
            <View
              style={[
                styles.colourPersonaWrap,
                { backgroundColor: colors.primary + '18' },
              ]}
            >
              <Ionicons
                name="color-wand-outline"
                size={12}
                color={colors.primary}
              />
              <Text
                style={[styles.colourPersonaText, { color: colors.primary }]}
                numberOfLines={1}
              >
                {[seasonLabel, undertoneLabel].filter(Boolean).join(' · ')}
              </Text>
            </View>
          ) : null}
          {isEditingBio ? (
            <View style={styles.bioEditContainer}>
              <TextField
                ref={bioInputRef}
                value={editedBio}
                onChangeText={setEditedBio}
                placeholder="Add a short bio..."
                multiline
                maxLength={150}
                containerStyle={styles.bioInput as Record<string, unknown>}
                style={styles.bioInputField}
              />
              <View style={styles.bioEditActions}>
                <TouchableOpacity
                  onPress={handleCancelBio}
                  style={[
                    styles.bioActionButton,
                    { borderColor: colors.border },
                  ]}
                  disabled={savingBio}
                >
                  <Ionicons name="close" size={16} color={colors.secondary} />
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={handleSaveBio}
                  style={[
                    styles.bioActionButton,
                    styles.saveBioButton,
                    { backgroundColor: colors.primary },
                  ]}
                  disabled={savingBio}
                >
                  {savingBio ? (
                    <ActivityIndicator size="small" color="white" />
                  ) : (
                    <Ionicons name="checkmark" size={16} color="white" />
                  )}
                </TouchableOpacity>
              </View>
            </View>
          ) : (
            <TouchableOpacity
              onPress={() => setIsEditingBio(true)}
              style={styles.bioTouch}
              activeOpacity={0.7}
            >
              <Text
                style={[
                  styles.userSubtitle,
                  { color: colors.secondary },
                  !editedBio && { fontStyle: 'italic' },
                ]}
                numberOfLines={2}
              >
                {editedBio || 'Add a short bio…'}
              </Text>
              <Ionicons
                name="create-outline"
                size={14}
                color={colors.secondary}
              />
            </TouchableOpacity>
          )}
        </View>
      </View>
    </View>
  );

  const renderProfileContentAfterIdentity = () => (
    <>
      {/* Plan card */}
      <View
        style={[
          styles.planCard,
          isMobile && styles.planCardMobile,
          { backgroundColor: colors.card, borderColor: colors.border },
        ]}
      >
        <View style={styles.planRow}>
          <View>
            <Text style={[styles.planLabel, { color: colors.secondary }]}>
              Subscription
            </Text>
            <Text style={[styles.planValue, { color: colors.text }]}>
              {planLabel}
            </Text>
          </View>
          {!paymentsKillSwitchOn && (
            <TouchableOpacity
              style={[
                styles.upgradeButtonWrap,
                { borderColor: colors.primary },
              ]}
              onPress={() => navigation.navigate('Pricing')}
              activeOpacity={0.7}
            >
              <Text
                style={[styles.upgradeButtonLabel, { color: colors.primary }]}
              >
                {user?.subscription_plan === 'pro' ? 'Manage' : 'Upgrade'}
              </Text>
              <Ionicons
                name="chevron-forward"
                size={16}
                color={colors.primary}
              />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Insights / Eureka */}
      {renderInsightsSection()}

      {/* Activity — premium dashboard section */}
      <View
        style={[
          styles.activityCard,
          isMobile && styles.activityCardMobile,
          { backgroundColor: colors.card, borderColor: colors.border },
        ]}
      >
        <View
          style={[
            styles.activityCardHeader,
            isMobile && styles.activityCardHeaderMobile,
            !isMobile && styles.activityCardHeaderDesktop,
          ]}
        >
          <View style={styles.activityCardTitleRow}>
            <Text style={[styles.activityCardTitle, { color: colors.text }]}>
              Activity
            </Text>
            {!toolkitLoading && (
              <View
                style={[
                  styles.activityProgressPill,
                  {
                    backgroundColor:
                      toolsUsedCount === trackableToolsCount
                        ? colors.primary + '18'
                        : colors.border,
                  },
                ]}
              >
                <Ionicons
                  name={
                    toolsUsedCount === trackableToolsCount
                      ? 'checkmark-circle'
                      : 'pie-chart-outline'
                  }
                  size={14}
                  color={
                    toolsUsedCount === trackableToolsCount
                      ? colors.primary
                      : colors.secondary
                  }
                />
                <Text
                  style={[
                    styles.activityProgressPillText,
                    {
                      color:
                        toolsUsedCount === trackableToolsCount
                          ? colors.primary
                          : colors.secondary,
                    },
                  ]}
                  numberOfLines={1}
                >
                  {activityProgressLabel}
                </Text>
              </View>
            )}
          </View>
          <Text
            style={[
              styles.activityCardSubtitle,
              !isMobile && styles.activityCardSubtitleDesktop,
              { color: colors.secondary },
            ]}
          >
            Your progress across the ColourSense toolkit
          </Text>
        </View>
        {toolkitLoading ? (
          <View style={styles.activityLoader}>
            <ActivityIndicator size="small" color={colors.primary} />
          </View>
        ) : (
          <>
            <Text
              style={[styles.activityGroupLabel, { color: colors.secondary }]}
            >
              Discover
            </Text>
            <View style={[styles.activityList, { borderColor: colors.border }]}>
              {activityDiscoverItems.map((item, index) => (
                <TouchableOpacity
                  key={item.key}
                  style={[
                    styles.activityRow,
                    { borderColor: colors.border },
                    index < activityDiscoverItems.length - 1 &&
                      styles.activityRowBorder,
                  ]}
                  onPress={item.navigate}
                  activeOpacity={0.7}
                >
                  <View
                    style={[
                      styles.activityRowIconWrap,
                      { backgroundColor: item.color + '18' },
                    ]}
                  >
                    <Ionicons name={item.icon} size={20} color={item.color} />
                  </View>
                  <View style={styles.activityRowContent}>
                    <Text
                      style={[styles.activityRowLabel, { color: colors.text }]}
                      numberOfLines={1}
                    >
                      {item.label}
                    </Text>
                    <Text
                      style={[
                        styles.activityRowMeta,
                        { color: colors.secondary },
                      ]}
                      numberOfLines={1}
                    >
                      {item.valueLabel}
                    </Text>
                  </View>
                  <Ionicons
                    name="chevron-forward"
                    size={18}
                    color={colors.secondary}
                  />
                </TouchableOpacity>
              ))}
            </View>
          </>
        )}
      </View>
    </>
  );

  const renderHeaderSection = () => (
    <>
      {renderIdentityBlock()}
      {renderProfileContentAfterIdentity()}
    </>
  );

  const hasAnalysisData = !!(
    recentAnalysis ||
    (profiles && profiles.length > 0)
  );
  const insightProfile = (recentAnalysis || profiles?.[0]) as
    | {
        palette?: SavedPaletteExtras;
        recommendations?: {
          season?: string;
          undertone?: string;
        };
        recommended_colors?: string[];
        dominant_colours?: unknown[];
        created_at?: string;
      }
    | undefined;
  const recs = insightProfile?.recommendations;
  const insightPalette = insightProfile?.palette;
  const seasonLabel = recs?.season
    ? String(recs.season).charAt(0).toUpperCase() + String(recs.season).slice(1)
    : '';
  const undertoneLabel = recs?.undertone
    ? String(recs.undertone).charAt(0).toUpperCase() +
      String(recs.undertone).slice(1)
    : '';
  const recommendedCount = Array.isArray(insightProfile?.recommended_colors)
    ? insightProfile.recommended_colors.length
    : 0;
  const accessoryCount =
    (insightPalette?.hair_colors?.length ?? 0) +
    (insightPalette?.eye_colors?.length ?? 0) +
    Object.keys(insightPalette?.jewelry ?? {}).length;
  const dominantCount = Array.isArray(insightProfile?.dominant_colours)
    ? insightProfile.dominant_colours.length
    : 0;
  const lastAnalysedAt =
    (recentAnalysis as { created_at?: string } | null)?.created_at ||
    (profiles?.[0] as { created_at?: string } | undefined)?.created_at;

  const renderInsightsSection = () => {
    /* Hidden: "Your colour story" card
    if (
      hasAnalysisData &&
      (seasonLabel || lastAnalysedAt || recommendedCount > 0)
    ) {
      return (
        <View
          style={[
            styles.insightsCard,
            isMobile && styles.insightsCardMobile,
            { backgroundColor: colors.card, borderColor: colors.border },
          ]}
        >
          <View style={styles.insightsHeader}>
            <Ionicons name="sparkles" size={20} color={colors.primary} />
            <Text style={[styles.insightsTitle, { color: colors.text }]}>
              Your colour story
            </Text>
          </View>
          {seasonLabel ? (
            <Text style={[styles.insightsEureka, { color: colors.text }]}>
              You&apos;re a {seasonLabel}
              {undertoneLabel ? ` with ${undertoneLabel} undertones` : ''} —
              colours that match your natural warmth and contrast are your best
              allies.
            </Text>
          ) : null}
          {lastAnalysedAt ? (
            <Text style={[styles.insightsMeta, { color: colors.secondary }]}>
              Palette last updated {formatTimeAgo(lastAnalysedAt)}
            </Text>
          ) : null}
          {(recommendedCount > 0 || accessoryCount > 0 || dominantCount > 0) && (
            <View style={styles.insightsStats}>
              {dominantCount > 0 && (
                <View
                  style={[
                    styles.insightPill,
                    { backgroundColor: palette.softViolet + '30' },
                  ]}
                >
                  <Ionicons
                    name="color-palette"
                    size={14}
                    color={palette.softViolet}
                  />
                  <Text
                    style={[styles.insightPillText, { color: colors.text }]}
                  >
                    {dominantCount} key colour{dominantCount !== 1 ? 's' : ''}{' '}
                    in your palette
                  </Text>
                </View>
              )}
              {recommendedCount > 0 && (
                <View
                  style={[
                    styles.insightPill,
                    { backgroundColor: colors.primary + '18' },
                  ]}
                >
                  <Ionicons name="heart" size={14} color={colors.primary} />
                  <Text
                    style={[styles.insightPillText, { color: colors.text }]}
                  >
                    {recommendedCount} colours love you
                  </Text>
                </View>
              )}
              {accessoryCount > 0 && (
                <View
                  style={[
                    styles.insightPill,
                    { backgroundColor: colors.border },
                  ]}
                >
                  <Ionicons
                    name="diamond-outline"
                    size={14}
                    color={colors.secondary}
                  />
                  <Text
                    style={[styles.insightPillText, { color: colors.text }]}
                  >
                    {accessoryCount} accessory colour
                    {accessoryCount !== 1 ? 's' : ''} saved
                  </Text>
                </View>
              )}
            </View>
          )}
          {(user?.virtual_drape_used_at ||
            toolkitStatsWithAnalyses.moodboards > 0 ||
            toolkitStatsWithAnalyses.wardrobe > 0) && (
            <Text
              style={[styles.insightsEurekaSmall, { color: colors.secondary }]}
            >
              {user?.virtual_drape_used_at &&
                'You&apos;ve tried the virtual drape — great for testing new shades. '}
              {toolkitStatsWithAnalyses.moodboards > 0 &&
                `You&apos;re curating style — ${toolkitStatsWithAnalyses.moodboards} mood board${toolkitStatsWithAnalyses.moodboards !== 1 ? 's' : ''}. `}
              {toolkitStatsWithAnalyses.wardrobe > 0 &&
                `${toolkitStatsWithAnalyses.wardrobe} item${toolkitStatsWithAnalyses.wardrobe !== 1 ? 's' : ''} in your wardrobe.`}
            </Text>
          )}
          <Text style={[styles.insightsTip, { color: colors.secondary }]}>
            {seasonLabel
              ? 'Stick to your palette when shopping — you’ll feel more confident and look more rested.'
              : 'Your personalised palette is ready. Try the drape test to see colours on you in real time.'}
          </Text>
        </View>
      );
    }
    */
    if (toolkitStatsWithAnalyses.analyses === 0 && !recentAnalysis) {
      return (
        <View
          style={[
            styles.insightsCard,
            isMobile && styles.insightsCardMobile,
            styles.insightsCardCta,
            {
              backgroundColor: colors.primary + '12',
              borderColor: colors.primary + '40',
            },
          ]}
        >
          <Ionicons
            name="color-palette-outline"
            size={24}
            color={colors.primary}
          />
          <Text style={[styles.insightsCtaTitle, { color: colors.text }]}>
            Discover your palette
          </Text>
          <Text style={[styles.insightsCtaText, { color: colors.secondary }]}>
            Run your first colour analysis to unlock your season, your best
            colours, and personalised tips — all in one place.
          </Text>
          <TouchableOpacity
            style={[
              styles.insightsCtaButton,
              { backgroundColor: colors.primary },
            ]}
            onPress={navigateToColorAnalysis}
            activeOpacity={0.8}
          >
            <Text style={styles.insightsCtaButtonText}>
              Start colour analysis
            </Text>
            <Ionicons name="arrow-forward" size={16} color="white" />
          </TouchableOpacity>
        </View>
      );
    }
    return null;
  };

  const renderPaletteSkeleton = () => (
    <View
      style={[
        styles.paletteCard,
        styles.paletteCardSkeleton,
        { backgroundColor: colors.card, borderColor: colors.border },
      ]}
    >
      <View
        style={[styles.paletteCardHeader, { borderBottomColor: colors.border }]}
      >
        <View
          style={[styles.skeletonLine, { backgroundColor: colors.border }]}
        />
        <View
          style={[styles.skeletonLineShort, { backgroundColor: colors.border }]}
        />
      </View>
      <View style={styles.paletteSection}>
        <View
          style={[styles.skeletonLineShort, { backgroundColor: colors.border }]}
        />
        <View style={styles.skeletonColorRow}>
          {[1, 2, 3, 4, 5].map((i) => (
            <View
              key={i}
              style={[
                styles.skeletonColorSwatch,
                { backgroundColor: colors.border },
              ]}
            />
          ))}
        </View>
      </View>
      <View style={styles.paletteSection}>
        <View
          style={[styles.skeletonLineShort, { backgroundColor: colors.border }]}
        />
        <View style={styles.skeletonColorRow}>
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <View
              key={i}
              style={[
                styles.skeletonColorSwatch,
                { backgroundColor: colors.border },
              ]}
            />
          ))}
        </View>
      </View>
    </View>
  );

  const renderSavedColors = () => {
    const hasData = recentAnalysis || (profiles && profiles.length > 0);
    const showSkeleton = isLoadingRecent && !hasData;

    if (showSkeleton) {
      return renderPaletteSkeleton();
    }

    if (!hasData) {
      return (
        <View style={[styles.emptyState, { backgroundColor: colors.card }]}>
          <Image
            source={require('../../../assets/illustrations/girl-canvas-palette.png')}
            style={styles.emptyStateIllustration as ImageStyle}
            resizeMode="contain"
          />
          <Text style={[styles.emptyStateTitle, { color: colors.text }]}>
            No Colour Analysis Found
          </Text>
          <Text style={[styles.emptyStateText, { color: colors.secondary }]}>
            Find your perfect palette. Start your first colour analysis.
          </Text>
          <Button
            title="Start Colour Analysis"
            onPress={navigateToColorAnalysis}
            variant="primary"
            icon={<Ionicons name="color-palette" size={20} color="white" />}
            hoverColor={colors.primary}
            trackClick={true}
            trackingLocation="profile-screen"
            trackingData={{
              button_name: 'Start Colour Analysis Profile Screen',
              button_type: 'start_colour_analysis_profile_screen',
              screen: 'profile',
            }}
          />
        </View>
      );
    }

    // Use recentAnalysis if available (from backend), otherwise fall back to latestProfile
    const displayProfile = recentAnalysis || profiles[0];
    const latestProfile = profiles[0];

    const recentRecs = recentAnalysis?.recommendations as
      | { season?: string; undertone?: string }
      | undefined;
    const recentUndertone = recentRecs?.undertone;

    // Get palette type/season - prioritize recentAnalysis data
    const paletteType =
      recentRecs?.season ||
      (recentUndertone
        ? recentUndertone.charAt(0).toUpperCase() + recentUndertone.slice(1)
        : displayProfile && 'palette' in displayProfile
          ? displayProfile.palette?.type
          : latestProfile?.palette?.type || 'Custom');

    const season = recentRecs?.season || '';

    const paletteLabel = season ? `${season} | ${paletteType}` : paletteType;

    const savedPalette: SavedPaletteExtras =
      (displayProfile as { palette?: SavedPaletteExtras } | undefined)
        ?.palette ??
      (latestProfile as { palette?: SavedPaletteExtras } | undefined)?.palette ??
      {};
    const savedHairColors = savedPalette.hair_colors ?? [];
    const savedEyeColors = savedPalette.eye_colors ?? [];
    const jewelryEntries = Object.entries(savedPalette.jewelry ?? {}).filter(
      ([, hex]) => typeof hex === 'string' && hex.length > 0
    );

    const renderHexSwatchRow = (hexColors: string[], max = 10) => (
      <View style={styles.paletteColorRow}>
        {hexColors.slice(0, max).map((colorHex, index) => (
          <View key={`${colorHex}-${index}`} style={styles.paletteColorItem}>
            <View
              style={[
                styles.paletteColorSwatch,
                {
                  backgroundColor: colorHex,
                  borderColor: colors.border,
                },
              ]}
            />
          </View>
        ))}
      </View>
    );

    const renderEmptyPalette = (message: string) => (
      <View
        style={[styles.emptyPaletteContainer, { borderColor: colors.border }]}
      >
        <Text style={[styles.emptyPaletteText, { color: colors.secondary }]}>
          {message}
        </Text>
      </View>
    );

    return (
      <View
        style={[
          styles.paletteCard,
          isMobile && styles.paletteCardMobile,
          { backgroundColor: colors.card, borderColor: colors.border },
        ]}
      >
        {/* Card Header */}
        <View
          style={[
            styles.paletteCardHeader,
            isMobile && styles.paletteCardHeaderMobile,
            { borderBottomColor: colors.border },
          ]}
        >
          <Text style={[styles.paletteCardTitle, { color: colors.text }]}>
            My Colour Palette
          </Text>
          <Text
            style={[styles.paletteCardSubtitle, { color: colors.secondary }]}
          >
            {paletteLabel}
          </Text>
        </View>

        {/* Dominant Colors Section */}
        <View style={styles.paletteSection}>
          <Text style={[styles.paletteSectionTitle, { color: colors.text }]}>
            Dominant Colors
          </Text>
          {(() => {
            // Get dominant colors from displayProfile or latestProfile
            const dominantColors =
              displayProfile?.dominant_colours ||
              latestProfile?.dominant_colours ||
              [];

            // Ensure it's an array
            const colorsArray = Array.isArray(dominantColors)
              ? dominantColors
              : [];

            if (colorsArray.length === 0) {
              return (
                <View
                  style={[
                    styles.emptyPaletteContainer,
                    { borderColor: colors.border },
                  ]}
                >
                  <Text
                    style={[
                      styles.emptyPaletteText,
                      { color: colors.secondary },
                    ]}
                  >
                    No dominant colors available
                  </Text>
                </View>
              );
            }

            // Filter out black and gray colors, then take only the first one
            const filteredColors = colorsArray
              .filter(
                (color: {
                  r?: number;
                  g?: number;
                  b?: number;
                  rgb?: number[];
                }) => {
                  // Handle both formats: {rgb: [r, g, b], hex, percentage} and {r, g, b, hex}
                  let r = 0,
                    g = 0,
                    b = 0;

                  if (
                    color.r !== undefined &&
                    color.g !== undefined &&
                    color.b !== undefined
                  ) {
                    r = color.r;
                    g = color.g;
                    b = color.b;
                  } else if (color.rgb && Array.isArray(color.rgb)) {
                    r = color.rgb[0];
                    g = color.rgb[1];
                    b = color.rgb[2];
                  } else {
                    return false; // Skip if we can't parse RGB
                  }

                  // Filter out very dark colors (black) - if all RGB values are very low
                  if (r < 30 && g < 30 && b < 30) {
                    return false;
                  }

                  // Filter out gray colors - if RGB values are too similar (within 20 of each other)
                  const maxDiff = Math.max(
                    Math.abs(r - g),
                    Math.abs(r - b),
                    Math.abs(g - b)
                  );
                  if (maxDiff < 20) {
                    return false; // Too gray, skip it
                  }

                  return true; // Keep this color
                }
              )
              .slice(0, 1); // Show only 1 dominant color

            if (filteredColors.length === 0) {
              return (
                <View
                  style={[
                    styles.emptyPaletteContainer,
                    { borderColor: colors.border },
                  ]}
                >
                  <Text
                    style={[
                      styles.emptyPaletteText,
                      { color: colors.secondary },
                    ]}
                  >
                    No dominant colors available
                  </Text>
                </View>
              );
            }

            return (
              <View style={styles.paletteColorRow}>
                {filteredColors.map(
                  (
                    color: {
                      hex?: string;
                      r?: number;
                      g?: number;
                      b?: number;
                      rgb?: number[];
                    },
                    index: number
                  ) => {
                    // Handle both formats: {rgb: [r, g, b], hex, percentage} and {r, g, b, hex}
                    let hexColor = '#9E9E9E'; // neutral gray fallback

                    if (color.hex) {
                      hexColor = color.hex;
                    } else if (
                      color.r !== undefined &&
                      color.g !== undefined &&
                      color.b !== undefined
                    ) {
                      // Format: {r, g, b, hex}
                      hexColor = `#${[color.r, color.g, color.b].map((c: number) => Math.round(c).toString(16).padStart(2, '0')).join('')}`;
                    } else if (color.rgb && Array.isArray(color.rgb)) {
                      // Format: {rgb: [r, g, b], hex, percentage}
                      hexColor = `#${color.rgb.map((c: number) => Math.round(c).toString(16).padStart(2, '0')).join('')}`;
                    }

                    return (
                      <View key={index} style={styles.paletteColorItem}>
                        <View
                          style={[
                            styles.paletteColorSwatch,
                            {
                              backgroundColor: hexColor,
                              borderColor: colors.border,
                            },
                          ]}
                        />
                      </View>
                    );
                  }
                )}
              </View>
            );
          })()}
          <Text
            style={[styles.paletteDescription, { color: colors.secondary }]}
          >
            These are the primary colors detected in your skin tone analysis,
            forming the foundation of your personalized colour palette.
          </Text>
        </View>

        {/* Recommended Colors Section */}
        <View style={styles.paletteSection}>
          <Text style={[styles.paletteSectionTitle, { color: colors.text }]}>
            Recommended Colors
          </Text>
          {recentAnalysis?.recommended_colors &&
          recentAnalysis.recommended_colors.length > 0 ? (
            <>
              <View style={styles.paletteColorRow}>
                {recentAnalysis.recommended_colors
                  .slice(0, 10)
                  .map((colorHex: string, index: number) => (
                    <View key={index} style={styles.paletteColorItem}>
                      <View
                        style={[
                          styles.paletteColorSwatch,
                          {
                            backgroundColor: colorHex,
                            borderColor: colors.border,
                          },
                        ]}
                      />
                    </View>
                  ))}
              </View>
              <Text
                style={[styles.paletteDescription, { color: colors.secondary }]}
              >
                These colors complement your skin tone and undertone, enhancing
                your natural features and creating a harmonious appearance.
              </Text>
            </>
          ) : latestProfile?.recommended_colors &&
            latestProfile.recommended_colors.length > 0 ? (
            <>
              <View style={styles.paletteColorRow}>
                {latestProfile.recommended_colors
                  .slice(0, 10)
                  .map((colorHex: string, index: number) => (
                    <View key={index} style={styles.paletteColorItem}>
                      <View
                        style={[
                          styles.paletteColorSwatch,
                          {
                            backgroundColor: colorHex,
                            borderColor: colors.border,
                          },
                        ]}
                      />
                    </View>
                  ))}
              </View>
              <Text
                style={[styles.paletteDescription, { color: colors.secondary }]}
              >
                These colors complement your skin tone and undertone, enhancing
                your natural features and creating a harmonious appearance.
              </Text>
            </>
          ) : (
            <View
              style={[
                styles.emptyPaletteContainer,
                { borderColor: colors.border },
              ]}
            >
              <Text
                style={[styles.emptyPaletteText, { color: colors.secondary }]}
              >
                No recommended colors available
              </Text>
            </View>
          )}
        </View>

        {/* Jewelry Section */}
        <View style={styles.paletteSection}>
          <Text style={[styles.paletteSectionTitle, { color: colors.text }]}>
            Jewelry
          </Text>
          {jewelryEntries.length > 0 ? (
            <>
              <View style={styles.jewelryRow}>
                {jewelryEntries.map(([key, hex]) => (
                  <View key={key} style={styles.jewelrySwatchWrap}>
                    <JewelryMetalSwatch
                      jewelryKey={key}
                      hex={hex}
                      style={styles.jewelrySwatch}
                    />
                  </View>
                ))}
              </View>
              <Text
                style={[styles.paletteDescription, { color: colors.secondary }]}
              >
                Metal tones that complement your undertone and enhance your
                natural colouring.
              </Text>
            </>
          ) : (
            renderEmptyPalette('No jewelry colours available')
          )}
        </View>

        {/* Hair Colors Section */}
        <View style={styles.paletteSection}>
          <Text style={[styles.paletteSectionTitle, { color: colors.text }]}>
            Hair Colors
          </Text>
          {savedHairColors.length > 0 ? (
            <>
              {renderHexSwatchRow(savedHairColors)}
              <Text
                style={[styles.paletteDescription, { color: colors.secondary }]}
              >
                Hair shades that harmonise with your skin tone and seasonal
                palette.
              </Text>
            </>
          ) : (
            renderEmptyPalette('No hair colours available')
          )}
        </View>

        {/* Eye Colors Section */}
        <View style={styles.paletteSection}>
          <Text style={[styles.paletteSectionTitle, { color: colors.text }]}>
            Eye Colors
          </Text>
          {savedEyeColors.length > 0 ? (
            <>
              {renderHexSwatchRow(savedEyeColors)}
              <Text
                style={[styles.paletteDescription, { color: colors.secondary }]}
              >
                Eye colour accents that bring out your natural features.
              </Text>
            </>
          ) : (
            renderEmptyPalette('No eye colours available')
          )}
        </View>
      </View>
    );
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <ResponsiveContainer
        style={styles.container}
        contentStyle={isMobile ? styles.fullWidth : undefined}
      >
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[
            styles.contentContainer,
            { paddingTop: scrollTopPadding },
          ]}
          showsVerticalScrollIndicator={Platform.OS === 'web'}
          showsHorizontalScrollIndicator={false}
        >
          <PageHero
            embedded
            title="Profile"
            subtitle={
              isMobile
                ? 'Your account and colour story.'
                : 'Your account, colour story, and activity across the toolkit.'
            }
          />
          {isMobile ? (
            <>
              {renderIdentityBlock()}
              <View
                style={[
                  styles.accountActionsCard,
                  isMobile && styles.accountActionsCardMobile,
                  { backgroundColor: colors.card, borderColor: colors.border },
                ]}
              >
                <TouchableOpacity
                  style={[
                    styles.accountActionRow,
                    isMobile && styles.accountActionRowMobile,
                    { borderBottomColor: colors.border },
                  ]}
                  onPress={() => navigation.navigate('Settings')}
                  activeOpacity={0.7}
                >
                  <Ionicons
                    name="settings-outline"
                    size={22}
                    color={colors.text}
                  />
                  <Text
                    style={[styles.accountActionLabel, { color: colors.text }]}
                  >
                    Settings
                  </Text>
                  <Ionicons
                    name="chevron-forward"
                    size={20}
                    color={colors.secondary}
                  />
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.accountActionRow,
                    isMobile && styles.accountActionRowMobile,
                    { borderBottomColor: colors.border },
                  ]}
                  onPress={handleInvitePress}
                  activeOpacity={0.7}
                >
                  <Ionicons
                    name="share-social-outline"
                    size={22}
                    color={colors.text}
                  />
                  <Text
                    style={[styles.accountActionLabel, { color: colors.text }]}
                  >
                    Invite friends
                  </Text>
                  <Ionicons
                    name="chevron-forward"
                    size={20}
                    color={colors.secondary}
                  />
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.accountActionRow,
                    isMobile && styles.accountActionRowMobile,
                    styles.accountActionRowLast,
                  ]}
                  onPress={async () => {
                    try {
                      await signOut();
                    } catch {
                      // ignore
                    }
                  }}
                  activeOpacity={0.7}
                >
                  <Ionicons
                    name="log-out-outline"
                    size={22}
                    color={colors.errorText}
                  />
                  <Text
                    style={[
                      styles.accountActionLabel,
                      { color: colors.errorText },
                    ]}
                  >
                    Log out
                  </Text>
                  <Ionicons
                    name="chevron-forward"
                    size={20}
                    color={colors.secondary}
                  />
                </TouchableOpacity>
              </View>
              {renderProfileContentAfterIdentity()}
            </>
          ) : (
            renderHeaderSection()
          )}
          {renderSavedColors()}
        </ScrollView>

        {/* Toast Notification */}
        {showToast && (
          <Animated.View
            style={[
              styles.toastContainer,
              Platform.OS === 'web' &&
                ({ position: 'fixed' } as Record<string, unknown>),
              {
                transform: [{ translateY: toastAnim }],
              },
            ]}
          >
            <View
              style={[styles.toastContent, { backgroundColor: colors.primary }]}
            >
              <Ionicons name="checkmark-circle" size={20} color="white" />
              <Text style={styles.toastText}>{toastMessage}</Text>
            </View>
          </Animated.View>
        )}
      </ResponsiveContainer>

      {/* Invite modal (mobile): overlay on Profile so Profile stays visible behind */}
      {isMobile && inviteLink && (
        <InviteModal
          visible={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          inviteLink={inviteLink}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  identityCard: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 16,
    marginBottom: 16,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
    elevation: 3,
  },
  identityRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
  },
  identityInfo: {
    flex: 1,
    minWidth: 0,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 2,
  },
  userEmail: {
    fontSize: 13,
    marginBottom: 8,
  },
  colourPersonaWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 20,
    marginBottom: 8,
  },
  colourPersonaText: {
    fontSize: 12,
    fontWeight: '600',
  },
  bioTouch: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
  },
  planCard: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 16,
    marginBottom: 16,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
    elevation: 3,
  },
  accountActionsCard: {
    borderRadius: 16,
    borderWidth: 1,
    marginBottom: 16,
    overflow: 'hidden',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
    elevation: 3,
  },
  accountActionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    minHeight: 52,
  },
  accountActionLabel: {
    flex: 1,
    fontSize: 16,
    fontWeight: '500',
    marginLeft: 14,
  },
  accountActionRowLast: {
    borderBottomWidth: 0,
  },
  planRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  planLabel: {
    fontSize: 12,
    fontWeight: '500',
    marginBottom: 2,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  planValue: {
    fontSize: 18,
    fontWeight: '700',
  },
  activityCard: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 16,
    marginBottom: 24,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
    elevation: 3,
  },
  activityCardHeader: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 6,
    marginBottom: 12,
    width: '100%',
  },
  activityCardHeaderDesktop: {
    gap: 8,
    marginBottom: 20,
  },
  activityCardTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    width: '100%',
  },
  activityCardTitle: {
    flex: 1,
    minWidth: 0,
    fontSize: 20,
    fontWeight: '700',
    letterSpacing: -0.3,
  },
  activityCardSubtitle: {
    fontSize: 13,
    lineHeight: 18,
    width: '100%',
  },
  activityCardSubtitleDesktop: {
    maxWidth: 240,
  },
  activityProgressPill: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 0,
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 20,
  },
  activityProgressPillText: {
    fontSize: 12,
    fontWeight: '600',
  },
  activityLoader: {
    paddingVertical: 24,
    alignItems: 'center',
  },
  activityGroupLabel: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 10,
    marginTop: 4,
  },
  activityList: {
    borderRadius: 12,
    borderWidth: 1,
    overflow: 'hidden',
    marginBottom: 20,
  },
  activityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 14,
    gap: 12,
  },
  activityRowBorder: {
    borderBottomWidth: 1,
  },
  activityRowIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  activityRowContent: {
    flex: 1,
    minWidth: 0,
  },
  activityRowLabel: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 2,
  },
  activityRowMeta: {
    fontSize: 12,
    fontWeight: '500',
  },
  insightsCard: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 16,
    marginBottom: 16,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
    elevation: 3,
  },
  insightsCardCta: {
    alignItems: 'center',
    paddingVertical: 24,
  },
  insightsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  insightsTitle: {
    fontSize: 17,
    fontWeight: '700',
  },
  insightsEureka: {
    fontSize: 15,
    lineHeight: 22,
    marginBottom: 8,
  },
  insightsMeta: {
    fontSize: 13,
    marginBottom: 12,
  },
  insightsStats: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  insightPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 20,
  },
  insightPillText: {
    fontSize: 13,
    fontWeight: '500',
  },
  insightsEurekaSmall: {
    fontSize: 13,
    lineHeight: 20,
    marginBottom: 10,
  },
  insightsTip: {
    fontSize: 13,
    lineHeight: 20,
    fontStyle: 'italic',
  },
  insightsCtaTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginTop: 12,
    marginBottom: 6,
    textAlign: 'center',
  },
  insightsCtaText: {
    fontSize: 14,
    lineHeight: 21,
    textAlign: 'center',
    marginBottom: 16,
    paddingHorizontal: 8,
  },
  insightsCtaButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 12,
  },
  insightsCtaButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: 'white',
  },
  profileHeader: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    marginHorizontal: 0,
    marginBottom: 24,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  profileTopSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  avatarContainer: {
    position: 'relative',
  },
  avatarEditWrapper: {
    position: 'relative',
  },
  editableAvatar: {
    width: 72,
    height: 72,
    borderRadius: 36,
    borderWidth: 1.5,
  },
  uploadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 36,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  editIconBadge: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#FFFFFF',
  },
  profileInfo: {
    flex: 1,
    gap: 0,
    minWidth: 0,
    alignSelf: 'flex-start',
  },
  userName: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 2,
    marginTop: 4,
  },
  nameDisplayContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 2,
    marginTop: 4,
  },
  nameClickableArea: {
    flex: 1,
  },
  nameEditContainer: {
    marginBottom: 8,
    width: '100%',
  },
  nameEditRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  nameInput: {
    marginVertical: 0,
    minHeight: 36,
    maxHeight: 36,
    marginBottom: 0,
    flex: 1,
  },
  nameInputField: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    minHeight: 36,
    maxHeight: 36,
    fontSize: 16,
    fontWeight: '700',
  },
  nameEditActions: {
    flexDirection: 'row',
    gap: 6,
    alignItems: 'center',
    flexShrink: 0,
  },
  nameActionButton: {
    width: 32,
    height: 32,
    borderRadius: 6,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  saveNameButton: {
    borderWidth: 0,
  },
  nameEditIcon: {
    width: 24,
    height: 24,
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  userSubtitle: {
    fontSize: 12,
    fontWeight: '500',
    marginTop: 0,
    flex: 1,
  },
  bioDisplayContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 0,
  },
  bioEditIcon: {
    width: 24,
    height: 24,
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  bioEditContainer: {
    marginTop: 0,
    width: '100%',
  },
  bioEditRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  bioInput: {
    marginVertical: 0,
    minHeight: 36,
    maxHeight: 36,
    marginBottom: 0,
    flex: 1,
  },
  bioInputField: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    minHeight: 36,
    maxHeight: 36,
    fontSize: 13,
  },
  bioEditActions: {
    flexDirection: 'row',
    gap: 6,
    alignItems: 'center',
    flexShrink: 0,
  },
  bioActionButton: {
    width: 32,
    height: 32,
    borderRadius: 6,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  saveBioButton: {
    borderWidth: 0,
  },
  loadingContainer: {
    alignItems: 'center',
    padding: 48,
    gap: 16,
  },
  loadingText: {
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
  },
  emptyState: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 16,
    marginBottom: 24,
    gap: 16,
  },
  emptyStateIllustration: {
    width: 320,
    height: 220,
    marginBottom: 8,
  },
  emptyStateTitle: {
    fontSize: 16,
    fontWeight: '700',
    textAlign: 'center',
  },
  emptyStateText: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
  },
  savedColorsContainer: {
    backgroundColor: 'transparent',
  },
  paletteCard: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 16,
    marginBottom: 24,
    marginHorizontal: Platform.OS === 'web' ? 0 : 0,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  paletteCardHeader: {
    marginBottom: Platform.OS === 'web' ? 20 : 16,
    paddingBottom: Platform.OS === 'web' ? 16 : 12,
    borderBottomWidth: 1,
  },
  paletteCardTitle: {
    fontSize: Platform.OS === 'web' ? 20 : 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  paletteCardSubtitle: {
    fontSize: Platform.OS === 'web' ? 15 : 13,
    fontWeight: '500',
  },
  paletteSection: {
    marginBottom: Platform.OS === 'web' ? 24 : 18,
  },
  paletteSectionTitle: {
    fontSize: Platform.OS === 'web' ? 15 : 14,
    fontWeight: '600',
    marginBottom: Platform.OS === 'web' ? 14 : 10,
  },
  paletteDescription: {
    fontSize: Platform.OS === 'web' ? 13 : 12,
    lineHeight: Platform.OS === 'web' ? 18 : 16,
    marginTop: 12,
    fontStyle: 'italic',
  },
  paletteColorRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Platform.OS === 'web' ? 10 : 8,
    justifyContent: 'flex-start',
  },
  paletteColorItem: {
    width: Platform.OS === 'web' ? 48 : 36,
    height: Platform.OS === 'web' ? 48 : 36,
  },
  paletteColorSwatch: {
    width: '100%',
    height: '100%',
    borderRadius: Platform.OS === 'web' ? 10 : 8,
    borderWidth: 1.5,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  jewelryRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Platform.OS === 'web' ? 12 : 10,
    alignItems: 'center',
  },
  jewelrySwatchWrap: {
    width: Platform.OS === 'web' ? 48 : 44,
    height: Platform.OS === 'web' ? 48 : 44,
  },
  jewelrySwatch: {
    width: '100%',
    height: '100%',
    borderRadius: Platform.OS === 'web' ? 10 : 8,
  },
  paletteCardSkeleton: {
    minHeight: 200,
  },
  skeletonLine: {
    height: 20,
    borderRadius: 4,
    width: '60%',
    marginBottom: 8,
  },
  skeletonLineShort: {
    height: 14,
    borderRadius: 4,
    width: '40%',
    marginBottom: 12,
  },
  skeletonColorRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
  },
  skeletonColorSwatch: {
    width: 40,
    height: 40,
    borderRadius: 8,
  },
  // Legacy styles kept for backward compatibility
  sectionHeader: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: 14,
    fontWeight: '500',
  },
  timestamp: {
    fontSize: 12,
    fontWeight: '400',
    marginTop: 4,
    fontStyle: 'italic',
  },
  colorSection: {
    marginBottom: 32,
  },
  colorSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 16,
  },
  colorSwatchRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  colorSwatchContainer: {
    alignItems: 'center',
    marginBottom: 8,
  },
  colorSwatch: {
    width: 60,
    height: 60,
    borderRadius: 12,
    marginBottom: 8,
    borderWidth: 2,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  colorHex: {
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center',
  },
  paletteContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  paletteColor: {
    width: 50,
    height: 50,
    borderRadius: 10,
    borderWidth: 2,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  emptyPaletteContainer: {
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    borderWidth: 1,
    borderStyle: 'dashed',
  },
  emptyPaletteContainerLeft: {
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    padding: 12,
  },
  emptyPaletteText: {
    fontSize: 12,
    fontStyle: 'italic',
    textAlign: 'center',
  },
  emptyPaletteTextLeft: {
    textAlign: 'left',
  },
  recommendationsGrid: {
    gap: 16,
  },
  recommendationColumn: {
    gap: 12,
  },
  recommendationTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  recommendationItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  recommendationColor: {
    width: 32,
    height: 32,
    borderRadius: 8,
    borderWidth: 2,
  },
  recommendationText: {
    fontSize: 12,
    fontWeight: '500',
  },
  contentContainer: {
    paddingHorizontal: 16,
    paddingBottom: 100,
    maxWidth: 1200,
    width: '100%',
    alignSelf: 'center',
  },
  fullWidth: {
    width: '100%',
  },
  identityCardMobile: {
    marginBottom: 12,
  },
  planCardMobile: {
    marginBottom: 12,
  },
  accountActionsCardMobile: {
    marginBottom: 12,
  },
  accountActionRowMobile: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    minHeight: 48,
  },
  activityCardMobile: {
    marginBottom: 12,
  },
  activityCardHeaderMobile: {
    marginBottom: 12,
  },
  insightsCardMobile: {
    marginBottom: 12,
  },
  paletteCardMobile: {
    marginBottom: 12,
  },
  paletteCardHeaderMobile: {
    marginBottom: 12,
    paddingBottom: 10,
  },
  subscriptionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 16,
    marginTop: 16,
    borderTopWidth: 1,
  },
  subscriptionPlanWrap: {
    flex: 1,
  },
  subscriptionLabel: {
    fontSize: 12,
    fontWeight: '500',
    marginBottom: 2,
  },
  subscriptionPlan: {
    fontSize: 16,
    fontWeight: '700',
  },
  upgradeButtonWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1.5,
  },
  upgradeButtonLabel: {
    fontSize: 14,
    fontWeight: '600',
  },
  toastContainer: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    zIndex: 1000,
  },
  toastContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
    gap: 8,
  },
  toastText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
});

export default ProfileScreen;
